import { Button } from '@lms/ui/button';
import { cn } from '@lms/ui/utils';
import { ChevronLeft } from 'lucide-react';
import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';

const boxSizes = {
  default: 'max-w-screen-2xl',
  xs: 'max-w-lg',
  sm: 'max-w-screen-lg',
} as const;

type BoxSize = keyof typeof boxSizes;

export interface PageBoxProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: BoxSize;
}

const PageBox = React.forwardRef<HTMLDivElement, PageBoxProps>(
  ({ className, size = 'default', ...props }, ref) => {
    return (
      <div
        className={cn(
          'mx-auto grid w-full auto-rows-max gap-4',
          boxSizes[size],
          className,
        )}
        ref={ref}
        {...props}
      />
    );
  },
);
PageBox.displayName = 'PageBox';

export interface PageHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  backHref?: string;
}

function PageHeader({
  className,
  children,
  backHref,
  ...props
}: PageHeaderProps) {
  const navigate = useNavigate();
  return (
    <div className={cn('flex items-center gap-4', className)} {...props}>
      {backHref && (
        <Button
          variant="outline"
          size="icon"
          className="h-7 w-7"
          asChild
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            navigate(-1);
          }}
        >
          <Link to={backHref}>
            <ChevronLeft className="h-4 w-4" />
          </Link>
        </Button>
      )}
      {children}
    </div>
  );
}
PageHeader.displayName = 'PageHeader';

const PageTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h1
    className={cn(
      'flex-1 shrink-0 whitespace-nowrap text-xl font-semibold tracking-tight sm:grow-0',
      className,
    )}
    {...props}
    ref={ref}
  />
));
PageTitle.displayName = 'PageTitle';

const PageButtons = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    className={cn(
      'hiddens items-center justify-end gap-2 md:ml-auto md:flex',
      className,
    )}
    {...props}
    ref={ref}
  />
));
PageButtons.displayName = 'PageButtons';

export { PageBox, PageHeader, PageTitle, PageButtons };
